// ================================================================================================
import { PureComponent } from 'react';
import _ from 'lodash';
import { DashboardManager } from './DashboardManager';
import { getRandomSmallKey, isDefined, IsNullOrWhiteSpace } from '@Utils/Micro';
import { Canvas, CanvasButtonEvent, CanvasLayout } from '@UI/Canvas/Canvas';
import type { DashboardDescriptor, DashboardDescriptorDictionary } from './DashboardStructs';
// ================================================================================================
export interface PageReportingProps {
    width: number;
    height: number;

    /**
     * Page Reporting descriptor
     */
    ReportPage: DashboardDescriptor;

    /**
     * All the pages
     */
    Pages: DashboardDescriptorDictionary;

    /**
     * Page is in edit mode.
     * This make the internal state in edit mode with editing support.
     */
    inEditMode: boolean;

    /**
     * CB for changes passing to dashboard manager.
     * Valid in edit mode.
     */
    onPageChange: (newPage: DashboardDescriptor) => void;

    /**
     * Selected widget for settings.
     */
    settingsKey: string;

    handleCanvasButtonTap: (key: string, type: CanvasButtonEvent) => void;
}
// ------------------------------------------------------------------------------------------------
export class PageReporting extends PureComponent<PageReportingProps>{
    private canvasKey: string;
    constructor(props: PageReportingProps) {
        super(props);
        this.canvasKey = getRandomSmallKey();
    }
    // --------------------------------------------------------------------------------------------
    onDashboardLayoutChanges = (newLayout: CanvasLayout, reportPageKey: string) => {
        if (isDefined(this.props.ReportPage.ViewWidgets.ReportPages)) {
            let reportPage = this.props.ReportPage.ViewWidgets.ReportPages[reportPageKey];
            if (isDefined(reportPage)) {
                let layoutIsNotEqual = !_.isEqual(reportPage.Layout, newLayout.LayoutDesc);
                if (!layoutIsNotEqual) {
                    reportPage.Layout = _.mapValues(newLayout.LayoutDesc, (value) => {
                        return {
                            H: value.H,
                            IsDraggable: value.IsDraggable,
                            IsResizable: value.IsResizable,
                            IsStatic: value.IsStatic,
                            Key: value.Key,
                            MaxH: value.MaxH,
                            MaxW: value.MaxW,
                            MinH: value.MinH,
                            MinW: value.MinW,
                            W: value.W,
                            X: value.X,
                            Y: value.Y,
                        };
                    })
                    this.props.onPageChange(this.props.ReportPage);
                }
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    render() {
        let dashboard = this.props.ReportPage;
        return <div id="section-to-print" style={{
            width: this.props.width,
            height: this.props.height,
            backgroundColor: "gray", overflow: "auto"
        }}>
            {
                _.map(this.props.ReportPage.ViewWidgets.ReportPages, (report, reportKey) => {
                    // A4 size
                    const pagePPI = 96; // Note this need to be the same with less
                    const pageRatio = 1.414;
                    const pageWidth = Math.floor(8.27 * pagePPI);
                    const pageHeight = Math.floor(pageWidth * pageRatio);
                    let inEditMode = this.props.inEditMode;
                    let reportPage: Partial<DashboardDescriptor> = {
                        Settings: this.props.ReportPage.Settings,
                        ViewWidgets: {
                            Layout: report.Layout,
                            WidgetsDesc: report.WidgetsDesc
                        },
                        Widgets: this.props.ReportPage.Widgets
                    };

                    if (!IsNullOrWhiteSpace(report.PageProxyGUID)) {
                        let p = _.find(this.props.Pages, p => p.Settings.PageId == report.PageProxyGUID);
                        if (isDefined(p)) {
                            reportPage = p;
                        }

                        // Page portals can't be editable
                        inEditMode = false;
                    }

                    // translate the widgets to canvas items
                    const Layout = DashboardManager.getLayout(reportPage as DashboardDescriptor, this.props.inEditMode, false, pageHeight, {}, true);

                    // Find if we have items in dashboard
                    if (Layout != null) {
                        // Set as a selected item the settings key
                        let SelectedItems: string[] = inEditMode && this.props.settingsKey != "" ? [this.props.settingsKey] : [];

                        return <div
                            key={reportKey}
                            id="render-page"
                            style={{ width: pageWidth, height: pageHeight, backgroundColor: dashboard.Settings.CanvasBackgroundColor }}>
                            <Canvas
                                key={this.canvasKey}
                                width={pageWidth}
                                height={pageHeight}
                                WidthListThreshold={0}
                                SizeW={12}
                                Layout={Layout.Layout}
                                CompactLayout={true}
                                SelectedItems={SelectedItems}
                                InEditMode={inEditMode}
                                Margin={dashboard.Settings.Margin}
                                onCanvasLayoutChanged={(newLayout) => this.onDashboardLayoutChanges(newLayout, reportKey)}
                                onButtonTap={this.props.handleCanvasButtonTap}
                                BackgroundImage={null}
                                GridLayout={true}
                                CanvasBackgroundColor={dashboard.Settings.CanvasBackgroundColor}
                                ElementSettings={{
                                    zDepth: 0,
                                    BackgroundColor: dashboard.Settings.WidgetBackgroundColor,
                                    NoBackground: dashboard.Settings.WidgetNoBackground,
                                    BorderRadius: dashboard.Settings.BorderRadius,
                                    Outline: dashboard.Settings.Outline
                                }}
                                HeightPixels={Layout.heightPixels}
                            />
                        </div>
                    }
                    return <div
                        id="render-page"
                        style={{ width: pageWidth, height: pageHeight }}>
                    </div>;
                })
            }
        </div>
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------